// src/features/admin/components/settings/system-preferences/SystemPreferencesPanel.tsx
import React, { useState, useEffect } from 'react';
import { 
  ComputerDesktopIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CalendarIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import RegionalSettings from './components/RegionalSettings';
import TypographySettings from './components/TypographySettings';
import DocumentDefaults from './components/DocumentDefaults';
import SecuritySettings from './components/SecuritySettings';
import CalendarSettings from './components/CalendarSettings';
import { 
  getSystemPreferences, 
  updateSystemPreferences,
  SystemPreferences 
} from '../../../services/SystemPreferencesAPI';
import { triggerPreferencesUpdate } from '../../../../../hooks/useSystemPreferences';
import { PreferencesContext } from './PreferencesContext';

export interface SystemPreferencesPanelProps {
  onSave?: (config: any) => void;
  onCancel?: () => void;
}

type SystemPreferencesSection = 'regional' | 'typography' | 'documents' | 'security' | 'calendar';

export const SystemPreferencesPanel: React.FC<SystemPreferencesPanelProps> = () => {
  const [activeSection, setActiveSection] = useState<SystemPreferencesSection>('typography');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<SystemPreferences | null>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getSystemPreferences();
      setPreferences(data);
      
      console.log('[SystemPreferences] Loaded preferences:', data);
    } catch (err: any) {
      console.error('Failed to load system preferences:', err);
      setError(err.message || 'Failed to load system preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = <K extends keyof SystemPreferences>(
    key: K, 
    value: SystemPreferences[K]
  ) => {
    setPreferences(prev => prev ? { ...prev, [key]: value } : null);
  };

  const handleSave = async () => {
    if (!preferences) {
      setError('No preferences to save');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const updatedPreferences = await updateSystemPreferences(preferences);
      setPreferences(updatedPreferences);
      
      // Trigger live update of preferences (applies changes immediately without refresh)
      triggerPreferencesUpdate(updatedPreferences);
      
      setSuccessMessage('System preferences saved and applied successfully');
      console.log('[SystemPreferences] Saved and applied preferences:', updatedPreferences);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Failed to save system preferences:', err);
      setError(err.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && !preferences) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading system preferences...</p>
        </div>
      </div>
    );
  }

  const contextValue = {
    preferences,
    updatePreference,
  };

  return (
    <PreferencesContext.Provider value={contextValue}>
    <div className="system-preferences-panel">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-600 pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <ComputerDesktopIcon className="h-8 w-8 text-lex-accent mr-3" />
              System Preferences
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure subscriber-wide defaults, operating contexts, service areas, and application behavior across all users.
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-lex-accent dark:border-lex-accent rounded-md text-sm font-medium text-lex-accent dark:text-lex-accent-contrast bg-lex-accent-soft dark:bg-lex-accent-soft hover:bg-lex-state-hover dark:hover:bg-lex-state-hover hover:border-lex-accent dark:hover:border-lex-accent transition-colors disabled:opacity-50"
              title="Save system preferences"
            >
              <CogIcon className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
          <div className="text-sm text-green-700 dark:text-green-300">{successMessage}</div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
        </div>
      )}

      {/* Navigation tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { 
              id: 'regional', 
              label: 'Regional & Localization',
              icon: GlobeAltIcon
            },
            { 
              id: 'typography', 
              label: 'Typography & Display',
              icon: DocumentTextIcon
            },
            { 
              id: 'documents', 
              label: 'Document Defaults',
              icon: DocumentTextIcon
            },
            { 
              id: 'security', 
              label: 'Security & Compliance',
              icon: ShieldCheckIcon
            },
            { 
              id: 'calendar', 
              label: 'Calendar & Scheduling',
              icon: CalendarIcon
            }
          ].map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as SystemPreferencesSection)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeSection === tab.id
                    ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content sections */}
      <div className="min-h-[400px]">
        {activeSection === 'regional' && (
          <div className="pt-6">
            <RegionalSettings />
          </div>
        )}

        {activeSection === 'typography' && (
          <div className="pt-6">
            <TypographySettings />
          </div>
        )}

        {activeSection === 'documents' && (
          <div className="pt-6">
            <DocumentDefaults />
          </div>
        )}

        {activeSection === 'security' && (
          <div className="pt-6">
            <SecuritySettings />
          </div>
        )}

        {activeSection === 'calendar' && (
          <div className="pt-6">
            <CalendarSettings />
          </div>
        )}
      </div>
    </div>
    </PreferencesContext.Provider>
  );
};

export default SystemPreferencesPanel;
